import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { LINKS } from "@/lib/site";
import { PlaybookGate } from "@/components/site/PlaybookGate";
import { PaintStroke } from "@/components/site/PaintStroke";
import { CountUp } from "@/components/site/CountUp";
import "./Hero.css";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stats = [
  { value: "$1.3M+", label: "Closed in sales" },
  { value: "140%", label: "Revenue growth" },
  { value: "2 weeks", label: "Kickoff to live" },
];

const Seal = () => (
  <div className="absolute right-10 top-28 z-20 hidden 2xl:block" data-cursor>
    <div className="relative h-28 w-28 md:h-36 md:w-36">
      <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full animate-spin-slow">
        <defs>
          <path id="circlePath" d="M 100,100 m -74,0 a 74,74 0 1,1 148,0 a 74,74 0 1,1 -148,0" />
        </defs>
        <text
          className="uppercase"
          fontFamily="'JetBrains Mono', ui-monospace, monospace"
          fontSize="14"
          letterSpacing="1.7"
          fill="#0F1115"
          xmlSpace="preserve"
        >
          <textPath href="#circlePath" textLength="465" lengthAdjust="spacing" xmlSpace="preserve">
            TOP 1% SALES TEAM • LIVE IN WEEKS •{" "}
          </textPath>
        </text>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="h-11 w-11 md:h-14 md:w-14 rounded-full bg-flame flex items-center justify-center">
          <ArrowDown size={22} className="text-white" strokeWidth={2.5} />
        </div>
      </div>
    </div>
  </div>
);

export const Hero = ({ ready = true }) => {
  const anim = ready ? "show" : "hidden";
  const soft = (delay) => (ready ? { opacity: 1, y: 0, transition: { delay, duration: 0.7 } } : { opacity: 0, y: 18 });

  return (
    <section id="top" className="relative pt-24 md:pt-28 overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        aria-hidden
        style={{
          backgroundImage:
            "linear-gradient(#0F1115 1px, transparent 1px), linear-gradient(90deg, #0F1115 1px, transparent 1px)",
          backgroundSize: "58px 58px",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-[-10%] h-[70vh] w-[70vh] rounded-full opacity-[0.07]"
        style={{
          background: "radial-gradient(circle, #1B36F9 0%, transparent 68%)",
        }}
      />
      <Seal />

      <div className="relative max-w-[1400px] mx-auto w-full px-5 md:px-10 pb-10 md:pb-14">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={soft(0.05)}
          className="hero-problem font-mono text-base md:text-lg font-medium uppercase tracking-[0.12em] text-ink mb-5 flex items-start gap-3 text-pretty"
        >
          <span className="hero-radar" aria-hidden>
            <span className="hero-radar-mark" />
          </span>
          <span>
            Building your own sales team takes years,{" "}
            <br className="hidden sm:block" />
            burns capital, and has a high failure rate.
          </span>
        </motion.p>

        <h1 className="hero-headline font-display font-semibold text-ink">
          <span className="block overflow-hidden">
            <motion.span custom={0} variants={line} initial="hidden" animate={anim} className="hero-headline-line block">
              <span className="hero-headline-opening">
                Your <span className="font-editorial italic font-medium text-cobalt">top 1%</span>
              </span>{" "}
              <span className="hero-headline-team relative inline-block">
                <PaintStroke id="hero-ps" className="pointer-events-none absolute left-0 right-0 bottom-[0.02em] h-[0.24em] w-full" />
                <span className="relative">sales team,</span>
              </span>
            </motion.span>
          </span>{" "}
          <span className="block overflow-hidden">
            <motion.span custom={1} variants={line} initial="hidden" animate={anim} className="hero-headline-line block">
              ready to go.
            </motion.span>
          </span>
        </h1>

        <div className="mt-7 md:mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={soft(0.7)}
              className="max-w-3xl text-base sm:text-lg md:text-xl font-light leading-[1.35] text-ink text-pretty"
            >
              <span className="block">We book meetings &amp; close deals for you.</span>
              <span className="block font-medium text-ink">2x faster. 2x more engagement.</span>
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 18 }} animate={soft(0.85)} className="hero-actions mt-6 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
              <a
                href={LINKS.cal}
                target="_blank"
                rel="noopener noreferrer"
                className="group box-border inline-flex h-14 w-full sm:w-auto items-center justify-center gap-2 bg-cobalt px-6 sm:px-7 font-mono text-[0.95rem] sm:text-base font-semibold uppercase tracking-[0.1em] sm:tracking-[0.12em] text-white border-2 border-ink transition-all duration-200 hover:-translate-y-1 hover:shadow-hard active:translate-y-0 active:shadow-none"
                data-testid="hero-book"
              >
                Book a meeting
                <ArrowUpRight size={18} className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <PlaybookGate
                triggerClassName="group relative box-border inline-flex h-14 w-full sm:w-auto items-center justify-center gap-2 border-2 border-flame bg-flame/10 px-6 sm:px-7 font-mono text-[0.95rem] sm:text-base font-semibold uppercase tracking-[0.1em] sm:tracking-[0.12em] text-ink transition-all duration-200 hover:bg-flame hover:-translate-y-1 hover:shadow-hard active:translate-y-0 active:shadow-none"
                label="Free $1M playbook"
                dataTestId="hero-playbook"
              />
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1, transition: { delay: 1.1, duration: 0.8 } } : { opacity: 0 }}
        className="relative border-t border-ink"
      >
        <motion.div
          aria-hidden
          className="absolute left-0 top-0 h-[2px] bg-gradient-to-r from-cobalt via-flame to-transparent"
          initial={{ scaleX: 0 }}
          animate={ready ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ delay: 1.2, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: "left" }}
        />
        <div className="max-w-[1400px] mx-auto w-full grid grid-cols-1 md:grid-cols-3">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`hero-stat min-w-0 px-5 md:px-8 lg:px-10 py-8 md:py-10 ${i > 0 ? "border-t md:border-t-0 md:border-l border-ink/15" : ""}`}
              data-testid={`hero-stat-${s.label}`}
            >
              <CountUp
                value={s.value}
                className="block font-display font-semibold text-5xl sm:text-6xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl text-cobalt tracking-tight tabular-nums leading-[0.9] whitespace-nowrap"
              />
              <span className="mt-3 block font-mono text-sm md:text-base lg:text-lg font-medium uppercase tracking-[0.12em] text-ink leading-snug">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
